import { ComponentProps, ComponentPropsWithoutRef } from 'react'
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro'
import { mtc } from '../../../utils/style-helpers'
import { Icon } from '../icon'
import { Button } from '../button'
import { Text } from '../text'
import {
  breadcrumbListStyles,
  breadcrumbItemStyles,
  breadcrumbLinkStyles,
  breadcrumbPageStyles,
  breadcrumbSeparatorStyles,
  breadcrumbEllipsisStyles,
} from './styles'

// Root component
const BreadcrumbRoot = ({ ...props }: ComponentPropsWithoutRef<'nav'>) => (
  <nav aria-label="breadcrumb" {...props} />
)

// Subcomponents
const BreadcrumbList = ({
  className,
  ...props
}: ComponentPropsWithoutRef<'ol'>) => (
  <ol className={mtc(breadcrumbListStyles(), className)} {...props} />
)

const BreadcrumbItem = ({
  className,
  ...props
}: ComponentPropsWithoutRef<'li'>) => (
  <li className={mtc(breadcrumbItemStyles(), className)} {...props} />
)

const BreadcrumbLink = ({
  className,
  onClick,
  ...props
}: ComponentPropsWithoutRef<'button'>) => (
  <Button
    onClick={onClick}
    className={mtc(breadcrumbLinkStyles(), className)}
    variant="ghost"
    {...props}
  />
)

const BreadcrumbPage = ({
  className,
  ...props
}: ComponentPropsWithoutRef<'span'>) => (
  <span
    role="link"
    aria-disabled="true"
    aria-current="page"
    className={mtc(breadcrumbPageStyles(), className)}
    {...props}
  />
)

const BreadcrumbSeparator = ({
  children,
  className,
  ...props
}: ComponentProps<'li'>) => (
  <li
    role="presentation"
    aria-hidden="true"
    className={mtc(breadcrumbSeparatorStyles(), className)}
    {...props}
  >
    {children ?? (
      <Icon icon={solid('chevron-right')} className="h-4 w-4" color="muted" />
    )}
  </li>
)

const BreadcrumbEllipsis = ({
  className,
  ...props
}: ComponentProps<'span'>) => (
  <span
    role="presentation"
    aria-hidden="true"
    className={mtc(breadcrumbEllipsisStyles(), className)}
    {...props}
  >
    <Icon icon={solid('ellipsis')} className="h-4 w-4" color="muted" />
    <Text className="sr-only">More</Text>
  </span>
)

// Attach subcomponents to root
export const Breadcrumb = Object.assign(BreadcrumbRoot, {
  List: BreadcrumbList,
  Item: BreadcrumbItem,
  Link: BreadcrumbLink,
  Page: BreadcrumbPage,
  Separator: BreadcrumbSeparator,
  Ellipsis: BreadcrumbEllipsis,
})
